import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { createImageElement } from "../utils/imageUtils";

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: #f5f5f5;
  border-radius: 8px;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s ease, transform 0.3s ease;
  opacity: ${(props) => (props.loaded ? 1 : 0)};
  transform: ${(props) => (props.loaded ? "scale(1)" : "scale(1.05)")};
`;

const LoadingPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    90deg,
    #f5f5f5 25%,
    #e8e8e8 50%,
    #f5f5f5 75%
  );
  background-size: 200% 100%;
  animation: ${(props) => (props.show ? "shimmer 1.5s infinite" : "none")};

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

const LoadingText = styled.div`
  color: #999;
  font-size: 0.9rem;
  font-weight: 500;
`;

const ErrorPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f8f8f8;
  color: #666;
  border: 2px dashed #ddd;
`;

const ErrorIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

const ErrorText = styled.div`
  font-size: 0.8rem;
  text-align: center;
  max-width: 80%;
`;

/**
 * LazyImage Component
 *
 * A staff engineer would implement this with:
 * - Intersection Observer for lazy loading
 * - Progressive loading states
 * - Error handling with fallbacks
 * - Performance optimizations
 * - Accessibility features
 */
const LazyImage = ({
	src,
	alt,
	className,
	style,
	onLoad,
	onError,
	placeholder,
	errorFallback,
	loadingThreshold = 0.1,
	fadeInDuration = 300,
	...props
}) => {
	const [loaded, setLoaded] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [inView, setInView] = useState(false);
	const imageRef = useRef(null);
	const containerRef = useRef(null);

	// Intersection Observer for lazy loading
	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setInView(true);
					observer.disconnect();
				}
			},
			{
				threshold: loadingThreshold,
				rootMargin: "50px", // Start loading 50px before image is visible
			},
		);

		if (containerRef.current) {
			observer.observe(containerRef.current);
		}

		return () => observer.disconnect();
	}, [loadingThreshold]);

	// Load image when in view
	useEffect(() => {
		if (inView && src && !loaded && !loading && !error) {
			setLoading(true);

			const img = createImageElement();

			img.onload = () => {
				setLoading(false);
				setLoaded(true);
				onLoad?.(img);
			};

			img.onerror = (err) => {
				setLoading(false);
				setError(true);
				onError?.(err);
			};

			img.src = src;
		}
	}, [inView, src, loaded, loading, error, onLoad, onError]);

	const handleImageLoad = () => {
		setLoaded(true);
		setLoading(false);
	};

	const handleImageError = (err) => {
		setError(true);
		setLoading(false);
		onError?.(err);
	};

	return (
		<ImageContainer ref={containerRef} className={className} style={style}>
			{/* Show loading placeholder when loading */}
			{(loading || (!inView && !loaded)) && (
				<LoadingPlaceholder show={loading}>
					{placeholder || <LoadingText>Loading...</LoadingText>}
				</LoadingPlaceholder>
			)}

			{/* Show error placeholder when failed */}
			{error && (
				<ErrorPlaceholder>
					{errorFallback || (
						<>
							<ErrorIcon>⚠️</ErrorIcon>
							<ErrorText>
								Failed to load image
								<br />
								<small>{alt}</small>
							</ErrorText>
						</>
					)}
				</ErrorPlaceholder>
			)}

			{/* Show image when in view and has src */}
			{inView && src && !error && (
				<Image
					ref={imageRef}
					src={src}
					alt={alt}
					loaded={loaded}
					onLoad={handleImageLoad}
					onError={handleImageError}
					style={{
						transitionDuration: `${fadeInDuration}ms`,
					}}
					{...props}
				/>
			)}
		</ImageContainer>
	);
};

export default LazyImage;

// Additional utility components for common use cases

export const GalleryImage = styled(LazyImage)`
  aspect-ratio: 4/3;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
`;

export const ThumbnailImage = styled(LazyImage)`
  aspect-ratio: 1;
  border-radius: 8px;
  cursor: pointer;
`;

export const HeroImage = styled(LazyImage)`
  aspect-ratio: 16/9;
  border-radius: 16px;

  @media (max-width: 768px) {
    aspect-ratio: 4/3;
  }
`;
